import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  BalansOmzetbelasting,
  BokkzVpbDataService,
  CorporateTaxEntity,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, Observable, shareReplay, tap} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule, DecimalPipe} from '@angular/common';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-omzetbelasting-passief',
  templateUrl: './spec-omzetbelasting-passief.component.html',
  styleUrls: ['./spec-omzetbelasting-passief.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiAlertModule,
    DecimalPipe,
    SharedUiDialogModule,
    SharedUiButtonModule,
    CommonModule
  ]
})
export class SpecOmzetbelastingPassiefComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public dialogVisible = false;

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte?.balansEnResultatenrekening?.schuldenOmzetbelasting?.at(0)) {
          this.omzetbelastingFg.patchValue(data.aangifte.balansEnResultatenrekening.schuldenOmzetbelasting[0], {emitEvent: false});
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public omzetbelastingFg = new FormGroup({
    omschrijving: new FormControl<string | null>(null),
    openingsbalans: new FormControl<number | null>(null),
    slotbalans: new FormControl<number | null>(null),
    nominaleWaardeOfKostenprijs: new FormControl<number | null>(null),
    ditBoekjaar: new FormControl<number | null>(null),
    vorigeBoekjaar: new FormControl<number | null>(null),
    oudereBoekjaren: new FormControl<number | null>(null)
  })

  showDialog() {
    this.dialogVisible = true;
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.omzetbelastingFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  getTotaal(): number {
    const slotbalans = this.omzetbelastingFg.get('slotbalans')?.value || 0
    const omzetbelastingDitBoekjaar = this.omzetbelastingFg.get('ditBoekjaar')?.value || 0
    const omzetbelastingVorigBoekjaar = this.omzetbelastingFg.get('vorigeBoekjaar')?.value || 0
    const omzetbelastingOudereBoekjaren = this.omzetbelastingFg.get('oudereBoekjaren')?.value || 0
    return slotbalans - omzetbelastingDitBoekjaar - omzetbelastingVorigBoekjaar - omzetbelastingOudereBoekjaren;
  }

  saveForm(): void {
    this.omzetbelastingFg.updateValueAndValidity({emitEvent: false});

    const formValue = this.omzetbelastingFg.getRawValue();
    const specificatie: BalansOmzetbelasting = {
      omschrijving: formValue.omschrijving?.trim() || undefined,
      openingsbalans: formValue.openingsbalans ?? undefined,
      slotbalans: formValue.slotbalans ?? undefined,
      nominaleWaardeOfKostenprijs: formValue.nominaleWaardeOfKostenprijs ?? undefined,
      ditBoekjaar: formValue.ditBoekjaar ?? undefined,
      vorigeBoekjaar: formValue.vorigeBoekjaar ?? undefined,
      oudereBoekjaren: formValue.oudereBoekjaren ?? undefined,
    };

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const huidigeSpecificatie = currentEntity.aangifte.balansEnResultatenrekening?.schuldenOmzetbelasting?.[0];

      const mergedSpecificatie: BalansOmzetbelasting = {
        ...huidigeSpecificatie,
        ...specificatie,
        // Preserve the main balance values when only the detailed specification is edited.
        openingsbalans: specificatie.openingsbalans ?? huidigeSpecificatie?.openingsbalans,
        slotbalans: specificatie.slotbalans ?? huidigeSpecificatie?.slotbalans,
      };

      const hasData = Object.values(mergedSpecificatie).some(value => value !== undefined);

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening: {
            ...currentEntity.aangifte.balansEnResultatenrekening,
            schuldenOmzetbelasting: hasData ? [mergedSpecificatie] : undefined,
          },
        },
      } as CorporateTaxEntity;
    });
  }
}
